import { Button, type ButtonProps, Checkbox, Divider, Group, ScrollArea, Stack, Text, TextInput } from "@mantine/core"
import { notifications } from "@mantine/notifications"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { type PropsWithChildren, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSlackImportTeam } from "@/hooks"
import { importAPISlackTeam, userAPI } from "@/utils"
import { AppLoader, icons, NoData } from "../utils"
import { UserAvatar } from "./UserAvatar"

interface IProps extends ButtonProps {
  onImport?: () => void
}

export function ImportUsers({ onImport, children, ...props }: PropsWithChildren<IProps>) {
  const { t } = useTranslation()

  const queryClient = useQueryClient()
  const { data, isFetching } = useSlackImportTeam()
  const doImport = useMutation({
    mutationFn: importAPISlackTeam,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [userAPI.url()] })
      queryClient.invalidateQueries({ queryKey: ["slack-import-users"] })
    },
  })
  const [choosen, setChoosen] = useState<string[]>([])
  const [search, setSearch] = useState("")
  const searched = data?.filter(
    search ? (user) => user.full_name?.toLowerCase().includes(search.toLowerCase()) : () => true,
  )

  return (
    <Stack>
      <Group wrap="nowrap">
        <TextInput
          w="100%"
          placeholder={t("Search users")}
          leftSection={<icons.Search size={20} />}
          value={search}
          disabled={isFetching}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
        <Checkbox
          mr="sm"
          disabled={isFetching || !data?.length}
          checked={!!(data?.length && choosen.length === data.length)}
          onChange={(e) => {
            setChoosen(e.currentTarget.checked ? data?.map((user) => user.id) || [] : [])
          }}
        />
      </Group>
      <ScrollArea h={300} w="100%">
        {isFetching ? (
          <AppLoader />
        ) : searched?.length ? (
          searched.map((user) => (
            <UserRow
              key={user.id}
              user={user}
              checked={choosen.includes(user.id)}
              onChange={(checked: boolean) => {
                if (checked) {
                  setChoosen([...choosen, user.id])
                } else {
                  setChoosen(choosen.filter((id) => id !== user.id))
                }
              }}
            />
          ))
        ) : (
          <NoData my="xl" />
        )}
      </ScrollArea>
      <Group justify="center">
        <Button
          size="lg"
          disabled={!choosen.length}
          onClick={async () => {
            await doImport.mutate(choosen)
            notifications.show({
              title: t("Import Successful"),
              message: t("Users imported: {{count}}", { count: choosen.length }),
              color: "green",
            })
            setChoosen([])
            onImport?.()
          }}
          {...props}
        >
          {t("Import selected")}
          {choosen.length ? ` (${choosen.length})` : ""}
        </Button>
        {children}
      </Group>
    </Stack>
  )
}

function UserRow({ user, checked, onChange }) {
  const toggle = () => onChange(!checked)
  return (
    <>
      <Divider />
      <Group my="xs" gap="xs" onClick={toggle} style={{ cursor: "pointer" }}>
        <UserAvatar user={user} />
        <Text>{user.full_name}</Text>
        <Checkbox ml="auto" mr="sm" checked={checked} onChange={toggle} />
      </Group>
    </>
  )
}
